export function getSurfaceTextureSize(width, height, limit) {
    const scale = Math.min(1, limit / Math.max(width, height));
    return [Math.max(1, Math.round(width * scale)), Math.max(1, Math.round(height * scale))];
}

function imageLength(value, extent) {
    if (!value || value === "auto") return null;
    const number = parseFloat(value);
    return Number.isFinite(number) ? Math.max(1, value.endsWith("%") ? extent * number / 100 : number) : null;
}

function imageOffset(value, free) {
    if (value === "left" || value === "top") return 0;
    if (value === "right" || value === "bottom") return free;
    if (value === "center" || !value) return free / 2;
    const number = parseFloat(value);
    return Number.isFinite(number) ? value.endsWith("%") ? free * number / 100 : number : free / 2;
}

// CSS pixels keep image scale and tiling independent of the selected scene quality.
export function getSurfaceImageLayout(surface, image, targetWidth, targetHeight) {
    const naturalWidth = image.naturalWidth || image.width, naturalHeight = image.naturalHeight || image.height;
    const sizing = String(surface.sizing || "cover").trim().toLowerCase();
    let width = naturalWidth, height = naturalHeight;
    if (sizing === "cover" || sizing === "contain") {
        const ratios = [targetWidth / naturalWidth, targetHeight / naturalHeight];
        const scale = sizing === "cover" ? Math.max(...ratios) : Math.min(...ratios);
        width *= scale;
        height *= scale;
    } else {
        const values = sizing.split(/\s+/);
        const w = imageLength(values[0], targetWidth), h = imageLength(values[1], targetHeight);
        if (w != null) width = w;
        if (h != null) height = h;
        if (w != null && h == null) height = naturalHeight * width / naturalWidth;
        if (h != null && w == null) width = naturalWidth * height / naturalHeight;
    }
    const repeat = surface.repeatMode || "no-repeat";
    if (repeat === "round") {
        width = targetWidth / Math.max(1, Math.round(targetWidth / width));
        height = targetHeight / Math.max(1, Math.round(targetHeight / height));
    }
    let [horizontal = "center", vertical = "center"] = String(surface.position || "center").trim().toLowerCase().split(/\s+/);
    if (horizontal === "top" || horizontal === "bottom") [horizontal, vertical] = [vertical, horizontal];
    let x = imageOffset(horizontal, targetWidth - width), y = imageOffset(vertical, targetHeight - height);
    let repeatX = ["repeat", "repeat-x", "round"].includes(repeat), repeatY = ["repeat", "repeat-y", "round"].includes(repeat);
    let gapX = 0, gapY = 0;
    if (repeat === "round") { x = 0; y = 0; }
    if (repeat === "space") {
        const columns = Math.floor(targetWidth / width), rows = Math.floor(targetHeight / height);
        if (columns >= 2) { repeatX = true; x = 0; gapX = (targetWidth - columns * width) / (columns - 1); }
        if (rows >= 2) { repeatY = true; y = 0; gapY = (targetHeight - rows * height) / (rows - 1); }
    }
    return { x, y, width, height, gapX, gapY, repeatX, repeatY };
}

export function drawSurfaceImage(context, image, surface, logicalWidth, logicalHeight) {
    const layout = getSurfaceImageLayout(surface, image, logicalWidth, logicalHeight);
    const scaleX = context.canvas.width / logicalWidth, scaleY = context.canvas.height / logicalHeight;
    const x = layout.x * scaleX, y = layout.y * scaleY, width = layout.width * scaleX, height = layout.height * scaleY;
    if (!layout.repeatX && !layout.repeatY) {
        context.drawImage(image, x, y, width, height);
        return;
    }
    const repetition = layout.repeatX && layout.repeatY ? "repeat" : layout.repeatX ? "repeat-x" : "repeat-y";
    let pattern;
    if (layout.gapX || layout.gapY) {
        const pitchX = layout.repeatX ? (layout.width + layout.gapX) * scaleX : context.canvas.width;
        const pitchY = layout.repeatY ? (layout.height + layout.gapY) * scaleY : context.canvas.height;
        const tile = document.createElement("canvas");
        tile.width = Math.max(1, Math.ceil(pitchX));
        tile.height = Math.max(1, Math.ceil(pitchY));
        const tileContext = tile.getContext("2d");
        if (!tileContext) throw new Error("Canvas textures are unavailable");
        tileContext.drawImage(image, layout.repeatX ? 0 : x, layout.repeatY ? 0 : y,
            width * tile.width / pitchX, height * tile.height / pitchY);
        pattern = context.createPattern(tile, repetition);
        pattern?.setTransform({ a: pitchX / tile.width, d: pitchY / tile.height, e: 0, f: 0 });
    } else {
        pattern = context.createPattern(image, repetition);
        pattern?.setTransform({ a: width / (image.naturalWidth || image.width), d: height / (image.naturalHeight || image.height), e: x, f: y });
    }
    if (!pattern) throw new Error("Canvas patterns are unavailable");
    context.fillStyle = pattern;
    context.fillRect(0, 0, context.canvas.width, context.canvas.height);
}

// Doorway sections share one full-wall image instead of repeating the image on each piece.
export function mapWallSection(geometry, side, height, left, right, bottom = 0, top = height) {
    const uv = geometry.attributes.uv;
    const u0 = (side / 2 - right) / side, u1 = (side / 2 - left) / side;
    uv.setXY(0, u0, top / height);
    uv.setXY(1, u1, top / height);
    uv.setXY(2, u0, bottom / height);
    uv.setXY(3, u1, bottom / height);
    uv.needsUpdate = true;
    return geometry;
}
